import { useId } from "react";
import type { ReactElement } from "react";
import type { LayerVisibility } from "../../state/layerState";
import type { InspectorPresentation } from "./BaseLayersCard";
import {
  InspectorChipButton,
  InspectorChipButtonRow
} from "./InspectorChipButton";
import { InspectorRow } from "./InspectorRow";
import { Spacer } from "../Spacer";
import { SubsectionHeader } from "./SubsectionHeader";

export const VIEWS_VISIBILITY_KEYS = [
  "viewshed",
  "viewpointMarker",
  "bestViewpoints",
  "sightlineArcs"
] as const;

export type ViewsVisibilityKey = (typeof VIEWS_VISIBILITY_KEYS)[number];

const LABELS: Record<ViewsVisibilityKey, string> = {
  viewshed: "Viewshed",
  viewpointMarker: "Viewpoint marker",
  bestViewpoints: "Best viewpoints",
  sightlineArcs: "Sightline arcs"
};

function snapEyeHeightFt(ft: number): number {
  const clamped = Math.min(30, Math.max(2, ft));
  const snapped = Math.round((clamped - 2) / 0.5) * 0.5 + 2;
  return Number(snapped.toFixed(2));
}

function formatEyeHeightBox(ft: number): string {
  const s = ft.toFixed(1);
  return s.endsWith(".0") ? String(Math.round(ft)) : s;
}

export interface ViewsLayersCardProps {
  visibility: LayerVisibility;
  eyeHeightFt: number;
  onToggle: (key: ViewsVisibilityKey, value: boolean) => void;
  /** During slider drag — updates `eyeHeightFt` without rebuilding views layers. */
  onEyeHeightLive: (ft: number) => void;
  /** After slider release / boxed value commit — triggers views rebuild (commit semantics). */
  onEyeHeightRebuild: () => void;
  onPickViewpoint: () => void;
  onResetViewpoint: () => void;
  presentation?: InspectorPresentation;
  /** Visibility keys whose value differs from the viewer default (accent dot). */
  modifiedKeys?: ReadonlySet<string>;
}

export function ViewsLayersCard({
  visibility,
  eyeHeightFt,
  onToggle,
  onEyeHeightLive,
  onEyeHeightRebuild,
  onPickViewpoint,
  onResetViewpoint,
  presentation = "inspector",
  modifiedKeys
}: ViewsLayersCardProps): ReactElement {
  const reactId = useId();
  const h = snapEyeHeightFt(eyeHeightFt);
  const drawerRail = presentation === "drawer";

  return (
    <div>
      {VIEWS_VISIBILITY_KEYS.map((key) => (
        <InspectorRow
          key={key}
          label={LABELS[key]}
          labelWeight="normal"
          checkboxId={`${reactId}-views-${key}`}
          checked={visibility[key]}
          modified={modifiedKeys?.has(key) === true}
          onCheckedChange={(v) => {
            onToggle(key, v);
          }}
        />
      ))}
      <SubsectionHeader label="Observer" />
      <InspectorRow
        label="Eye height (ft)"
        labelWeight="normal"
        fieldRow
        inspectorSliderTrailing={{
          sliderLabelWide: false,
          sliderId: `${reactId}-eye-r`,
          boxInputId: `${reactId}-eye-i`,
          ariaLabel: "Observer eye height in feet",
          min: 2,
          max: 30,
          step: 0.5,
          value: h,
          boxDisplayValue: formatEyeHeightBox(h),
          onSliderInput: (next) => {
            onEyeHeightLive(snapEyeHeightFt(next));
          },
          onSliderCommit: () => {
            onEyeHeightRebuild();
          },
          parseBoxValue: (raw) => {
            const n = Number.parseFloat(raw);
            if (!Number.isFinite(n)) return undefined;
            if (n < 2 || n > 30) return undefined;
            return snapEyeHeightFt(n);
          },
          formatBoxDraft: (v) => formatEyeHeightBox(snapEyeHeightFt(v)),
          onBoxParsed: (canonical) => {
            const v = snapEyeHeightFt(canonical);
            onEyeHeightLive(v);
            onEyeHeightRebuild();
          }
        }}
      />
      <Spacer size="xs" noBorder />
      <InspectorChipButtonRow>
        <InspectorChipButton
          drawerRail={drawerRail}
          onClick={() => {
            onPickViewpoint();
          }}
        >
          Set viewpoint
        </InspectorChipButton>
        <InspectorChipButton
          drawerRail={drawerRail}
          onClick={() => {
            onResetViewpoint();
          }}
        >
          Reset
        </InspectorChipButton>
      </InspectorChipButtonRow>
    </div>
  );
}
